import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { friendlyError } from '@/lib/errorMessage';
import { vaultsApi } from '@/api/vaults';
import {
  loadVaultDescriptorIfSupported,
  readWalletError,
  vaultDeviceRefusalCode,
  withVaultDescriptor,
} from '@/lib/vaultDescriptor';

interface ReservedNonce {
  kPublic: string;
  kTwoPublic: string;
}

/**
 * Parameters sent to the SSP Wallet extension to co-sign a vault MESSAGE
 * (WalletConnect Phase 2). This is the canonical contract the extension's
 * `enterprise_vault_sign_message` handler must implement — it mirrors
 * `enterprise_vault_sign_tx` but the thing signed is a 32-byte message digest
 * (EIP-191 personal_sign) rather than a UserOp hash, and there is NO broadcast.
 * VD-2: the vault descriptor (fetched for orgId/vaultId) rides along unchanged.
 */
export interface SignMessageParams {
  /** Vault references: select the vault descriptor sent to the devices. */
  orgId: string;
  vaultId: string;
  chain: string;
  orgIndex: number;
  vaultIndex: number;
  addressIndex: number;
  vaultName?: string;
  orgName?: string;
  /** 0x 32-byte EIP-191 digest to sign (also the ERC-1271 verification hash). */
  digest: string;
  /** Human-readable message text shown on the wallet + key approval screens. */
  displayMessage: string;
  /** dApp origin/name shown on the approval screens (anti-phishing context). */
  dappOrigin?: string;
  signingMode?: string;
  // EVM M-of-N: this signer's reserved nonces + all signers' public material.
  reservedNonce?: ReservedNonce;
  reservedKeyNonce?: ReservedNonce;
  allSignerKeys?: string[];
  allSignerNonces?: Array<{ kPublic: string; kTwoPublic: string }>;
}

export interface SignMessageResult {
  walletSignatures: string[];
  keySignatures: string[];
  walletPubKey: string;
  keyPubKey: string;
  wkIdentity: string;
}

interface UseVaultMessageSigningReturn {
  signMessage: (params: SignMessageParams) => Promise<SignMessageResult | null>;
  isLoading: boolean;
  error: string | null;
  /** The error of the last signMessage call, readable right after it resolves. */
  getLastError: () => string | null;
  clearError: () => void;
}

/**
 * Bridge to the SSP Wallet extension for vault message signing. Produces this
 * signer's partial Schnorr contribution over the message digest — the relay
 * aggregates the M contributions into the final ERC-1271 signature. The
 * enterprise app never holds keys; all signing stays behind window.ssp + SSP Key.
 */
export function useVaultMessageSigning(): UseVaultMessageSigningReturn {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastErrorRef = useRef<string | null>(null);

  const signMessage = useCallback(
    async (params: SignMessageParams): Promise<SignMessageResult | null> => {
      const fail = (message: string): null => {
        setError(message);
        lastErrorRef.current = message;
        return null;
      };
      if (typeof window.ssp === 'undefined') {
        return fail(t('errors.walletNotDetected'));
      }
      setIsLoading(true);
      setError(null);
      lastErrorRef.current = null;
      try {
        // VD-2: no descriptor → nothing is sent to the wallet. A wallet
        // without descriptor support (≤ 2.1.3) gets today's params.
        let descriptor;
        try {
          descriptor = await loadVaultDescriptorIfSupported(
            vaultsApi.getVaultDescriptor,
            { orgId: params.orgId, vaultId: params.vaultId },
            { orgIndex: params.orgIndex, vaultIndex: params.vaultIndex, chain: params.chain }
          );
        } catch (err) {
          return fail(friendlyError(err, t, 'errors.vaultDescriptorUnavailable'));
        }

        const requestParams: Record<string, unknown> = {
          chain: params.chain,
          orgIndex: params.orgIndex,
          vaultIndex: params.vaultIndex,
          addressIndex: params.addressIndex,
          vaultName: params.vaultName || '',
          orgName: params.orgName || '',
          digest: params.digest,
          message: params.displayMessage,
          dappOrigin: params.dappOrigin || '',
          origin: window.location.origin,
          siteName: 'SSP Enterprise',
          iconUrl: 'https://enterprise.sspwallet.com/ssp-logo-black-256x256.png',
        };
        if (params.signingMode) requestParams.signingMode = params.signingMode;
        if (params.reservedNonce) requestParams.reservedNonce = params.reservedNonce;
        if (params.reservedKeyNonce) requestParams.reservedKeyNonce = params.reservedKeyNonce;
        if (params.allSignerKeys?.length) {
          requestParams.allSignerKeys = JSON.stringify(params.allSignerKeys);
        }
        if (params.allSignerNonces?.length) {
          requestParams.allSignerNonces = JSON.stringify(params.allSignerNonces);
        }

        const response = await window.ssp.request(
          'enterprise_vault_sign_message',
          withVaultDescriptor(requestParams, descriptor)
        );

        if (response.status === 'SUCCESS' && response.result) {
          const result = response.result as Record<string, unknown>;
          if (
            !Array.isArray(result.walletSignatures) ||
            !Array.isArray(result.keySignatures) ||
            typeof result.walletPubKey !== 'string' ||
            typeof result.keyPubKey !== 'string' ||
            typeof result.wkIdentity !== 'string'
          ) {
            throw new Error(t('errors.invalidSigningResponse'));
          }
          return {
            walletSignatures: result.walletSignatures as string[],
            keySignatures: result.keySignatures as string[],
            walletPubKey: result.walletPubKey,
            keyPubKey: result.keyPubKey,
            wkIdentity: result.wkIdentity,
          };
        }

        const errorMessage =
          typeof response.result === 'string'
            ? response.result
            : typeof response.data === 'string'
              ? response.data
              : t('errors.signFailed');
        const refusal = vaultDeviceRefusalCode(
          errorMessage,
          typeof response.errorCode === 'string' ? response.errorCode : null
        );
        return fail(
          refusal ? friendlyError({ errorCode: refusal }, t, 'errors.signFailed') : errorMessage
        );
      } catch (err) {
        const walletError = readWalletError(err);
        const refusal = vaultDeviceRefusalCode(walletError.message, walletError.errorCode);
        return fail(friendlyError(refusal ? { errorCode: refusal } : err, t, 'errors.signFailed'));
      } finally {
        setIsLoading(false);
      }
    },
    [t]
  );

  const getLastError = useCallback(() => lastErrorRef.current, []);

  const clearError = useCallback(() => {
    setError(null);
    lastErrorRef.current = null;
  }, []);

  return { signMessage, isLoading, error, getLastError, clearError };
}

export default useVaultMessageSigning;
